//src/pages/cadastros/ClientCompaniesPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ChevronLeft, ChevronRight, Plus, Search } from "lucide-react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { CrudRowActions } from "@/components/shared/CrudRowActions";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageHeader } from "@/components/shared/PageHeader";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { useClientCompanies, useSetClientCompanyActive } from "@/hooks/useClientCompanies";
import { maskCNPJ, onlyDigits } from "@/lib/masks";
import { PERMISSIONS } from "@/lib/permissions";
import type { ClientCompanyListItem } from "@/services/clientCompanies";

type StatusFilter = "active" | "inactive" | "all";

const LIMIT = 50;

function normalizeText(v: string) {
  return (v || "").toLowerCase().trim();
}

function formatCNPJ(value?: string | null) {
  const digits = onlyDigits(value || "");
  if (!digits) return "-";
  return maskCNPJ(digits);
}

export default function ClientCompaniesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const userPermissions = user?.permissions ?? [];
  const canWriteClientCompanies = userPermissions.includes(PERMISSIONS.CLIENT_COMPANIES_WRITE);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [offset, setOffset] = useState(0);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState<ClientCompanyListItem | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, statusFilter]);

  const query = useClientCompanies({
    q: debouncedSearch,
    limit: LIMIT,
    offset,
  });

  const setActiveMut = useSetClientCompanyActive();
  const allRows = query.data ?? [];

  const filtered = useMemo(() => {
    const q = normalizeText(debouncedSearch);

    let base = allRows;
    if (statusFilter === "active") base = base.filter((r) => r.is_active);
    else if (statusFilter === "inactive") base = base.filter((r) => !r.is_active);

    if (!q) return base;

    return base.filter((r) => {
      const name = normalizeText(r.name);
      const legalName = normalizeText(r.legal_name ?? "");
      const doc = normalizeText(r.document_number ?? "");
      const slug = normalizeText(r.slug ?? "");

      return (
        name.includes(q) ||
        legalName.includes(q) ||
        slug.includes(q) ||
        doc.includes(q) ||
        onlyDigits(doc).includes(onlyDigits(q))
      );
    });
  }, [allRows, debouncedSearch, statusFilter]);

  const hasNextPage = allRows.length === LIMIT;
  const canPrev = offset > 0;
  const isBusy = query.isFetching || setActiveMut.isPending;

  function openToggle(row: ClientCompanyListItem) {
    if (!canWriteClientCompanies) return;
    setSelected(row);
    setConfirmOpen(true);
  }

  async function handleConfirmToggle() {
    if (!selected || !canWriteClientCompanies) return;

    const nextActive = !selected.is_active;

    try {
      await setActiveMut.mutateAsync({
        id: selected.id,
        is_active: nextActive,
      });

      toast.success(nextActive ? "Cliente ativado" : "Cliente desativado");
      setConfirmOpen(false);
      setSelected(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar status do cliente");
    }
  }

  return (
    <div>
      <PageHeader
        title="Clientes"
        description="Cadastro de clientes da consultoria vinculados ao tenant atual"
      >
        <PermissionButton
          permission={PERMISSIONS.CLIENT_COMPANIES_WRITE}
          onClick={() => navigate("/cadastros/clientes/novo")}
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Cliente
        </PermissionButton>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome fantasia, razão social ou CNPJ"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>

        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
            disabled={!canPrev || query.isFetching}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Anterior
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((o) => o + LIMIT)}
            disabled={!hasNextPage || query.isFetching}
          >
            Próxima
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>

      {query.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando clientes...
        </Card>
      ) : query.isError ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            {(query.error as any)?.message || "Erro ao carregar clientes"}
          </div>
          <div className="mt-3">
            <Button variant="outline" onClick={() => query.refetch()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="Nenhum cliente encontrado"
          description="Cadastre um cliente ou revise os filtros aplicados."
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[280px]">Cliente</TableHead>
                <TableHead className="w-[180px]">CNPJ</TableHead>
                <TableHead className="w-[180px]">Slug</TableHead>
                {user?.access_scope === "platform" ? <TableHead className="w-[220px]">Tenant</TableHead> : null}
                <TableHead className="w-[100px] text-center">Status</TableHead>
                <TableHead className="w-[170px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate">{r.name}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {r.legal_name ?? "-"}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {formatCNPJ(r.document_number)}
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {r.slug}
                  </TableCell>

                  {user?.access_scope === "platform" ? (
                    <TableCell className="text-sm text-muted-foreground">
                      {r.tenant_name ?? "-"}
                    </TableCell>
                  ) : null}

                  <TableCell className="text-center">
                    <Badge variant={r.is_active ? "default" : "secondary"} className="text-xs">
                      {r.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-right">
                    <CrudRowActions
                      onView={() => navigate(`/cadastros/clientes/${r.id}`)}
                      onEdit={() => navigate(`/cadastros/clientes/${r.id}/editar`)}
                      onToggleActive={() => openToggle(r)}
                      isActive={r.is_active}
                      disabled={isBusy}
                      viewPermission={PERMISSIONS.CLIENT_COMPANIES_READ}
                      editPermission={PERMISSIONS.CLIENT_COMPANIES_WRITE}
                      toggleActivePermission={PERMISSIONS.CLIENT_COMPANIES_WRITE}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={selected?.is_active ? "Desativar Cliente" : "Ativar Cliente"}
        description={
          selected?.is_active
            ? "Deseja desativar este cliente?"
            : "Deseja ativar este cliente novamente?"
        }
        onConfirm={handleConfirmToggle}
        confirmLabel={selected?.is_active ? "Desativar" : "Ativar"}
        variant={selected?.is_active ? "destructive" : "default"}
      />
    </div>
  );
}
